import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  inject,
  Self,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CartManagementMode, GetTenantGroupDto, TenantDetailDto } from '@flexyoo/api/models';
import { WhiteLabelService } from '@flexyoo/common';
import { TagItem } from '@flexyoo/shared/models';
import {
  LoaderComponent,
  TagsInputComponent,
  TENANT_AUTOCOMPLETE_SEARCH_SERVICE_TOKEN,
  TenantAutocompleteSearchComponent,
} from '@flexyoo/shared/ui';
import { TenantGroupHomeDataAccessService } from '@flexyoo/tenant-group/data-access';
import {
  TenantGroupCreateRequest,
  TenantGroupPossibleTenant,
  TenantGroupSettings,
  TenantGroupSettingsRequest,
  TenantGroupUpdateRequest,
  TenantGroupUpdateRequestTenant,
  TenantGroupUpdateRequestTenantUser,
} from '@flexyoo/tenant-group/models';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs';
import { NgOnDestroy } from 'src/app/shared/ng-on-destroy.service';
import { ITenantGroupFormValue } from '../../declarations/interfaces/tenant-group-form-dialog.interface';
import { TenantGroupFormDialogAction } from '../../declarations/types/tenant-group-form-dialog-action.type';
import { TenantGroupPossibleTenantsAutocompleteSearchService } from './tenant-group-possible-tenants-autocomplete-search.service';

type TenantGroupFormTenant = TenantGroupPossibleTenant | TenantDetailDto;

enum GroupFormTab {
  General = 0,
  Permissions = 1,
  Settings = 2,
}

@Component({
  selector: 'app-tenant-group-form-dialog',
  templateUrl: './tenant-group-form-dialog.component.html',
  styleUrl: './tenant-group-form-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    TranslateModule,
    MatDialogModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    FlexLayoutModule,
    AsyncPipe,
    LoaderComponent,
    TagsInputComponent,
    MatTabsModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatAccordion,
    MatExpansionModule,
    TenantAutocompleteSearchComponent,
  ],
  providers: [
    NgOnDestroy,
    TenantGroupPossibleTenantsAutocompleteSearchService,
    {
      provide: TENANT_AUTOCOMPLETE_SEARCH_SERVICE_TOKEN,
      useExisting: TenantGroupPossibleTenantsAutocompleteSearchService,
    },
  ],
})
export class TenantGroupFormDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly tenantGroupHomeService = inject(TenantGroupHomeDataAccessService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected readonly wls = inject(WhiteLabelService);
  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);

  protected readonly GroupFormTab = GroupFormTab;

  protected readonly form = this.fb.group<ITenantGroupFormValue>({
    name: this.fb.control(null, [Validators.required]),
    ownerTenantId: this.fb.control(null, [Validators.required]),
    tenantsIds: this.fb.control([], [Validators.required]),
    usersInfo: this.fb.group({}),
    useCustomPensionLegalDates: this.fb.control(false, { nonNullable: true }),
    useNextYearLegalDates: this.fb.control(false, { nonNullable: true }),
    useSingleCartValidation: this.fb.control(false, { nonNullable: true }),
    isHrApprovalRequired: this.fb.control({ value: false, disabled: true }, { nonNullable: true }),
    useWhiteLabel: this.fb.control(false, { nonNullable: true }),
  });

  protected readonly isSingleCartValidationOn = toSignal(
    this.form.controls.useSingleCartValidation.valueChanges.pipe(
      startWith(this.form.controls.useSingleCartValidation.value)
    ),
    { initialValue: this.form.controls.useSingleCartValidation.value }
  );

  private savedSingleCartValidation: boolean | null = null;
  private singleCartValidationDirty = false;
  private isPatchingSystemConfiguration = false;

  // The cart management mode may only be changed until the group's first choice period
  // has started.
  protected get isFirstChoicePeriodStarted(): boolean {
    return !!this.data.isFirstChoicePeriodStarted;
  }

  private readonly ownerTenantIdValue$ = this.form.controls.ownerTenantId.valueChanges.pipe(
    startWith(this.form.controls.ownerTenantId.value),
    map(() => this.form.controls.ownerTenantId.getRawValue()),
    distinctUntilChanged(),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  private readonly tenantIdsValue$ = this.form.controls.tenantsIds.valueChanges.pipe(
    startWith(this.form.controls.tenantsIds.value),
    map(() => this.form.controls.tenantsIds.getRawValue()),
    distinctUntilChanged()
  );

  private readonly possibleTenants$ = this.tenantGroupHomeService
    .getTenantGroupPossibleTenantsList()
    .pipe(
      map((tenants) =>
        this.data.action === 'edit' ? [...tenants, ...(this.data.item?.Tenants ?? [])] : tenants
      ),
      shareReplay({ refCount: true, bufferSize: 1 })
    );

  protected readonly tenantsTags$: Observable<TagItem[]> = combineLatest([
    this.possibleTenants$,
    this.ownerTenantIdValue$,
  ]).pipe(
    map(([tenants, ownerTenantId]) =>
      tenants.reduce((acc, tenant) => {
        if (tenant.Id === +ownerTenantId) {
          return acc;
        }
        acc.push({
          id: tenant.Id,
          name: tenant.Name,
        });
        return acc;
      }, [])
    )
  );

  protected readonly selectedTenants$: Observable<TenantGroupFormTenant[]> = combineLatest([
    this.possibleTenants$,
    this.ownerTenantIdValue$,
    this.tenantIdsValue$,
  ]).pipe(
    map(([tenants, ownerTenantId, tenantsIds]) =>
      tenants.filter((tenant) => tenant.Id === +ownerTenantId || tenantsIds.includes(tenant.Id))
    )
  );

  protected selectedTabIndex: GroupFormTab = GroupFormTab.General;

  constructor(
    @Self() private readonly onDestroy$: NgOnDestroy,
    @Inject(MAT_DIALOG_DATA)
    protected data: {
      action: TenantGroupFormDialogAction;
      item?: GetTenantGroupDto;
      settings: TenantGroupSettings;
      isFirstChoicePeriodStarted: boolean;
    },
    private readonly dialog: MatDialogRef<TenantGroupFormDialogComponent, boolean>
  ) {
    this.selectedTenants$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedTenants) => this.updateUserInfoControls(selectedTenants));

    this.form.controls.useSingleCartValidation.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((useSingleCart) => this.syncHrApprovalWithSingleCart(useSingleCart));

    if (data.item) {
      inject(TenantGroupPossibleTenantsAutocompleteSearchService).selectedTenant$.next(
        this.data.item?.Tenants ?? []
      );

      const useSingleCart = data.settings.CartManagementMode === CartManagementMode.Cart;
      this.savedSingleCartValidation = useSingleCart;
      this.singleCartValidationDirty = false;

      this.isPatchingSystemConfiguration = true;
      this.form.patchValue({
        ownerTenantId: data.item.GroupOwnerTenantId,
        name: data.item.Name,
        useCustomPensionLegalDates: data.settings.UseCustomPensionLegalDates,
        useSingleCartValidation: useSingleCart,
        useWhiteLabel: data.settings.UseWhiteLabel,
        useNextYearLegalDates: data.settings.UseNextYearLegalDates,
        tenantsIds: (data.item.Tenants ?? []).reduce((acc, t) => {
          if (t.Id !== data.item.GroupOwnerTenantId) {
            acc.push(t.Id);
          }
          return acc;
        }, [] as number[]),
      });
      this.setHrApprovalRequired(data.settings.IsHrApprovalRequired);
      this.isPatchingSystemConfiguration = false;

      if (data.action === 'edit') {
        this.form.controls.ownerTenantId.disable();
      }

      if (this.isFirstChoicePeriodStarted) {
        this.form.controls.useSingleCartValidation.disable();
      }
    }
  }

  private syncHrApprovalWithSingleCart(useSingleCart: boolean): void {
    if (this.isPatchingSystemConfiguration) {
      return;
    }

    if (this.savedSingleCartValidation === null) {
      this.setHrApprovalRequired(useSingleCart);
      return;
    }

    if (useSingleCart !== this.savedSingleCartValidation) {
      this.singleCartValidationDirty = true;
    }

    if (this.singleCartValidationDirty) {
      this.setHrApprovalRequired(useSingleCart);
    }
  }

  private setHrApprovalRequired(value: boolean): void {
    const control = this.form.controls.isHrApprovalRequired;
    control.enable({ emitEvent: false });
    control.setValue(value, { emitEvent: false });
    control.disable({ emitEvent: false });
    this.changeDetectorRef.markForCheck();
  }

  public saveBudget(): void {
    if (!this.form.valid) {
      return;
    }

    const formRawValue = this.form.getRawValue();
    const formTenantsData: TenantGroupUpdateRequestTenant[] = [
      formRawValue.ownerTenantId,
      ...formRawValue.tenantsIds,
    ].map((id) => {
      const data: TenantGroupUpdateRequestTenant = {
        TenantId: +id,
        UsersData: Object.entries(formRawValue.usersInfo[id]).map(([key, value]) => {
          const userData: TenantGroupUpdateRequestTenantUser = {
            UserId: key,
            IsGroupAdmin: value.isGroupAdmin,
          };

          return userData;
        }),
      };

      return data;
    });

    this.isLoading$.next(true);

    switch (this.data.action) {
      case 'create': {
        const request: TenantGroupCreateRequest = {
          Name: formRawValue.name,
          OwnerOfGroupTenantId: +formRawValue.ownerTenantId,
          TenantsData: formTenantsData,
        };

        this.tenantGroupHomeService
          .createTenantGroup(request)
          .pipe(
            switchMap((createdGroupId) =>
              this.tenantGroupHomeService.setTenantGroupSettings(
                createdGroupId,
                this.buildSettingsPayload()
              )
            ),
            catchError(() => of(false)),
            finalize(() => this.isLoading$.next(false))
          )
          .subscribe((isSaved) => {
            if (isSaved) {
              this.closeAfterUpdate();
            }
          });
        return;
      }
      case 'edit': {
        if (this.data.item) {
          const request: TenantGroupUpdateRequest = {
            Name: formRawValue.name,
            TenantsData: formTenantsData,
          };

          this.tenantGroupHomeService
            .updateTenantGroup(this.data.item.Id, request)
            .pipe(
              switchMap(() =>
                this.tenantGroupHomeService.setTenantGroupSettings(
                  this.data.item.Id,
                  this.buildSettingsPayload()
                )
              ),
              catchError(() => of(false)),
              finalize(() => this.isLoading$.next(false))
            )
            .subscribe((isSaved) => {
              if (isSaved) {
                this.closeAfterUpdate();
              }
            });
        }
        return;
      }
    }
  }

  public closeDialog(): void {
    this.dialog.close();
  }

  private buildSettingsPayload(): TenantGroupSettingsRequest {
    const formRawValue = this.form.getRawValue();
    const settings = this.data.settings;

    // The endpoint resets every setting that is missing from the payload, so the settings
    // this form does not manage are sent back as they were loaded. For a new group they
    // are unknown, null lets the API apply its own defaults.
    return {
      UseNextYearLegalDates: formRawValue.useNextYearLegalDates,
      UseCustomPensionLegalDates: formRawValue.useCustomPensionLegalDates,
      UseWhiteLabel: formRawValue.useWhiteLabel,
      CartManagementMode: formRawValue.useSingleCartValidation
        ? CartManagementMode.Cart
        : CartManagementMode.Benefit,
      MaxNumberOfActiveCarts: settings?.MaxNumberOfActiveCarts ?? null,
      IsHrApprovalRequired: formRawValue.isHrApprovalRequired,
      IsHrSignatureRequired: settings?.IsHrSignatureRequired ?? false,
    };
  }

  private closeAfterUpdate(): void {
    this.dialog.close(true);
  }

  private updateUserInfoControls(selectedTenants: TenantGroupFormTenant[]): void {
    Object.keys(this.form.controls.usersInfo.controls).forEach((ctrlTenantId) => {
      if (!selectedTenants.find((t) => t.Id.toString() === ctrlTenantId)) {
        this.form.controls.usersInfo.removeControl(ctrlTenantId);
      }
    });

    selectedTenants.forEach((tenant) => {
      if (!this.form.controls.usersInfo.controls[tenant.Id]) {
        this.form.controls.usersInfo.addControl(tenant.Id.toString(), this.fb.group({}));
      }
      (tenant.Users ?? []).forEach((user) => {
        if (!user.UserId) {
          return;
        }
        const userCtrl = this.form.controls.usersInfo.controls[tenant.Id].controls[user.UserId];
        if (!userCtrl) {
          this.form.controls.usersInfo.controls[tenant.Id].addControl(
            user.UserId,
            this.fb.group({
              isGroupAdmin: this.fb.control(user.IsGroupAdmin ?? false),
            })
          );
        }
      });
    });
  }
}
